import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    SxProps,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronLeft } from '@mui/icons-material';
import AvatarComponent from './avatar';
import { UserAreaMenuItem, UserAreaType } from '../../../types/sidebar.types';
import { Styled } from '../sidebar.styled';

const UserArea = ({
    menu,
    name,
    avatar,
    account,
    avatar_color,
}: UserAreaType) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (route = '') => {
        setAnchorEl(null);

        if (route.length > 1) navigate(route);
    };

    const MemorizedAvatarBox = useCallback(
        ({
            badge,
            nameSx,
            descSx,
        }: {
            badge?: boolean;
            nameSx?: SxProps;
            descSx?: SxProps;
        }) => {
            return (
                <AvatarBox
                    name={name}
                    avatar={avatar}
                    account={account}
                    avatar_color={avatar_color}
                    badge={badge}
                    nameSx={nameSx}
                    descSx={descSx}
                />
            );
        },
        [avatar, name, account, avatar_color],
    );

    return (
        <>
            <Styled.UserArea
                className={`user-area-button${open ? ' open' : ''}`}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                endIcon={
                    <Box component="span" className="account-chevron">
                        <ChevronLeft />
                    </Box>
                }
                onClick={handleClick}>
                <MemorizedAvatarBox badge />
            </Styled.UserArea>

            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={() => handleClose()}
                PaperProps={{
                    elevation: 0,
                    sx: (theme) => ({
                        width: 318,
                        maxWidth: 'calc(100vw - 24px)',
                        minHeight: 'fit-content',
                        mt: 0.75,
                        p: 0,
                        overflow: 'hidden',
                        borderRadius: '6px',
                        border: `1px solid ${theme.palette.border.light}`,
                        backgroundColor: alpha(
                            theme.palette.background.paper,
                            theme.palette.mode === 'dark' ? 0.98 : 0.99,
                        ),
                        backgroundImage:
                            theme.palette.mode === 'dark'
                                ? `linear-gradient(180deg, ${alpha(
                                      theme.palette.common.white,
                                      0.025,
                                  )} 0%, transparent 28%)`
                                : `linear-gradient(180deg, ${alpha(
                                      theme.palette.primary.main,
                                      0.025,
                                  )} 0%, transparent 32%)`,
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        boxShadow:
                            theme.palette.mode === 'dark'
                                ? `0 22px 58px ${alpha('#000', 0.48)}`
                                : `0 22px 58px ${alpha('#172033', 0.16)}`,
                        '& .MuiList-root': {
                            p: 1,
                            maxHeight: 'calc(100dvh - 24px)',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            scrollbarWidth: 'thin',
                        },
                    }),
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'top' }}>
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={(theme) => ({
                        mb: 0.65,
                        p: 1.25,
                        borderRadius: '6px',
                        border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.16 : 0.1,
                        )}`,
                        background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.14 : 0.07,
                        )} 0%, ${alpha(
                            theme.palette.background.extraLight ??
                                theme.palette.background.paper,
                            0.88,
                        )} 100%)`,
                    })}>
                    <MemorizedAvatarBox
                        nameSx={{
                            fontSize: 14,
                            lineHeight: 1.7,
                            fontWeight: 400,
                        }}
                        descSx={{
                            mt: 0.25,
                            fontSize: 11,
                            color: 'text.secondary',
                        }}
                    />
                </Box>

                {menu?.map((userAreaItem: UserAreaMenuItem, i: number) => {
                    const url = userAreaItem.url || '';

                    return (
                        <MenuItem
                            key={`${url}-${i}`}
                            onClick={() => handleClose(url)}
                            sx={(theme) => ({
                                minHeight: 46,
                                px: 1.05,
                                py: 0.55,
                                mb: 0.25,
                                gap: 0.9,
                                borderRadius: '6px',
                                alignItems: 'center',
                                border: '1px solid transparent',
                                transition: theme.transitions.create(
                                    [
                                        'background-color',
                                        'border-color',
                                        'transform',
                                    ],
                                    { duration: 140 },
                                ),
                                '&:hover': {
                                    transform: 'translateX(-1px)',
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        theme.palette.mode === 'dark'
                                            ? 0.085
                                            : 0.05,
                                    ),
                                    borderColor: alpha(
                                        theme.palette.primary.main,
                                        0.1,
                                    ),
                                    '& .user-menu-icon': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.12,
                                        ),
                                    },
                                    '& .user-menu-arrow': {
                                        opacity: 1,
                                        transform: 'translateX(-2px)',
                                    },
                                },
                            })}>
                            <ListItemIcon
                                className="user-menu-icon"
                                sx={(theme) => ({
                                    width: 34,
                                    height: 34,
                                    minWidth: '34px !important',
                                    m: 0,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: '4px',
                                    color: 'text.secondary',
                                    backgroundColor: alpha(
                                        theme.palette.text.secondary,
                                        0.06,
                                    ),
                                    border: `1px solid ${theme.palette.border.light}`,
                                    transition: theme.transitions.create(
                                        ['background-color', 'color'],
                                        { duration: 140 },
                                    ),
                                    '& i': {
                                        fontSize: 17,
                                    },
                                })}>
                                {userAreaItem.icon}
                            </ListItemIcon>

                            <ListItemText
                                disableTypography
                                sx={{ m: 0, minWidth: 0 }}
                                primary={
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 400,
                                            color: 'text.primary',
                                            lineHeight: 1.6,
                                        }}>
                                        {userAreaItem.label}
                                    </Typography>
                                }
                            />

                            <Box
                                className="user-menu-arrow"
                                sx={(theme) => ({
                                    flexShrink: 0,
                                    width: 24,
                                    height: 24,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: '4px',
                                    color: 'text.light',
                                    opacity: 0.55,
                                    transition: theme.transitions.create(
                                        ['opacity', 'transform'],
                                        { duration: 140 },
                                    ),
                                    '& i': {
                                        fontSize: 11,
                                    },
                                })}>
                                <i className="fa-light fa-angle-left" />
                            </Box>
                        </MenuItem>
                    );
                })}

                <Divider sx={{ my: 0.8, mx: 0.5, borderColor: 'border.light' }} />

                <LogOutMenuItem handleClose={handleClose} />
            </Menu>
        </>
    );
};

const AvatarBox: React.FC<
    Pick<
        UserAreaType,
        'name' | 'account' | 'avatar' | 'avatar_color' | 'badge'
    > & { nameSx?: SxProps; descSx?: SxProps }
> = ({ name, account, avatar, avatar_color, badge, nameSx, descSx }) => {
    const userAreaTextSx = {
        m: 0,
        fontFamily: 'YekanBalkhHeavy',
        fontWeight: 400,
        maxWidth: badge ? 105 : 205,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                '& .MuiAvatar-root': {
                    width: badge ? 42 : 46,
                    height: badge ? 42 : 46,
                    flexShrink: 0,
                },
            }}>
            <AvatarComponent
                avatar={avatar}
                name={name}
                avatar_color={avatar_color}
                badge={badge}
            />
            <Styled.UserAreaDescription className="description">
                <Tooltip title={name || ''} placement="left">
                    <Typography
                        className="font-yekan-heavy"
                        sx={{ ...userAreaTextSx, ...nameSx }}>
                        {name || 'کاربر'}
                    </Typography>
                </Tooltip>

                <Typography
                    sx={{ ...userAreaTextSx, ...descSx }}
                    fontSize={11.5}
                    color="text.light">
                    {account || '—'}
                </Typography>
            </Styled.UserAreaDescription>
        </Box>
    );
};

const LogOutMenuItem = ({ handleClose }: any) => {
    const handleLogOut = () => {
        handleClose();
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    return (
        <MenuItem
            onClick={handleLogOut}
            sx={(theme) => ({
                minHeight: 48,
                mx: 0.25,
                mb: 0.1,
                px: 1.25,
                gap: 1,
                borderRadius: '6px',
                color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`,
                backgroundColor: alpha(theme.palette.error.main, 0.035),
                '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                    borderColor: alpha(theme.palette.error.main, 0.2),
                },
            })}>
            <Box
                sx={(theme) => ({
                    width: 34,
                    height: 34,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '6px',
                    backgroundColor: alpha(theme.palette.error.main, 0.09),
                })}>
                <i className="fa-light fa-power-off" style={{ fontSize: 15 }} />
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 400 }}>
                خروج از حساب
            </Typography>
        </MenuItem>
    );
};

export default UserArea;
